import React, { useCallback, useEffect, useState } from 'react';
import { Menu, X, User } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
const WHATSAPP_URL = 'https://wa.me/77010346443?text=%D1%85%D0%BE%D1%87%D1%83%20%D0%B4%D0%B5%D0%BC%D0%BE%20Meelo';
type NavSection = {
  id: string;
  key: string;
  href?: string;
};
const SECTIONS: NavSection[] = [{
  id: 'what-is-meelo',
  key: 'nav.about'
}, {
  id: 'solutions',
  key: 'nav.solutions'
}, {
  id: 'find-solution',
  key: 'nav.find_solution',
  href: '#/find-solution'
}, {
  id: 'contact',
  key: 'nav.contact'
}];
function getActiveSection(): string {
  const offset = 120;
  let active = '';
  for (const {
    id
  } of SECTIONS) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el.getBoundingClientRect().top <= offset) active = id;
  }
  return active;
}
function getScrollProgress(): number {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (docHeight <= 0) return 0;
  return Math.min(100, scrollTop / docHeight * 100);
}
export function Navbar() {
  const {
    t
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 10);
    setScrollProgress(getScrollProgress());
    setActiveSection(getActiveSection());
  }, []);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  const linkClass = (id: string) => activeSection === id ? 'text-sm font-medium text-[#7C4DFF] bg-[#7C4DFF]/10 rounded-full px-3 py-1 transition-colors whitespace-nowrap' : 'text-sm font-medium text-gray-700 hover:text-[#7C4DFF] transition-colors whitespace-nowrap px-3 py-1';
  const close = () => setIsMobileMenuOpen(false);
  const NAV_LINKS = [...SECTIONS.map(s => ({
    href: s.href ?? `#${s.id}`,
    label: t(s.key),
    external: false
  })), {
    href: '#/mission',
    label: t('nav.mission'),
    external: false
  }];
  return <>
      {/* ── Full-screen mobile overlay ─────────────────────────────────── */}
      <div aria-hidden={!isMobileMenuOpen} style={{
      position: 'fixed',
      inset: 0,
      zIndex: 80,
      background: 'rgba(255,255,255,0.88)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(-100%)',
      opacity: isMobileMenuOpen ? 1 : 0,
      transition: prefersReducedMotion ? 'none' : 'transform 280ms cubic-bezier(0.4,0,0.2,1), opacity 280ms ease',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} className="md:hidden" data-id="element-69" data-mp="2ndtfllx74pz">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4" data-id="element-70" data-mp="o2gz8ye0k0b9">
          <a href="#" onClick={close} data-id="element-71" data-mp="1btcopouzyjfc">
            <img src="https://cdn.magicpatterns.com/uploads/k1HQQhYUAYkCqHQP8zEE8y/meelo_logo_transparent.png" alt="Meelo" className="h-10 w-auto" data-id="element-72" data-mp="1gkunc69js7x5" />
          </a>
          <div className="flex items-center gap-3" data-id="element-73" data-mp="3k33xd6nmcp6u">
            <LanguageSwitcher data-id="element-74" data-mp="3k1j37i57v89i" />
            <button onClick={close} aria-label="Закрыть меню" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#EDE7F6] hover:text-[#7C4DFF] transition-all" style={{
            transform: isMobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: prefersReducedMotion ? 'none' : 'transform 280ms ease'
          }} data-id="element-75" data-mp="3f3reewzvc29j">
              <X size={20} data-id="element-76" data-mp="3u19y3kxovpa4" />
            </button>
          </div>
        </div>

        {/* Thin divider */}
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-77" data-mp="ue1yshrcccfs" />

        {/* Nav links */}
        <nav className="flex-1 flex flex-col justify-center px-8 gap-1" data-id="element-78" data-mp="plqwj894oek2">
          {NAV_LINKS.map(({
          href,
          label
        }) => <a key={href} href={href} onClick={close} className="group flex items-center py-4 text-2xl font-semibold text-[#1A1A2E] hover:text-[#7C4DFF] transition-colors border-b border-gray-100 last:border-0" style={{
          letterSpacing: '-0.01em'
        }} data-id="element-79" data-mp="28beeupvnn9qa">
              <span className="transition-transform duration-150 group-active:scale-[1.02]" data-id="element-80" data-mp="2r97f1kew95jz">
                {label}
              </span>
              <span className="ml-auto text-gray-300 group-hover:text-[#7C4DFF]/40 transition-colors text-lg" data-id="element-81" data-mp="2w5o88t0yaa3x">
                →
              </span>
            </a>)}
        </nav>

        {/* Sign in + Phone / CTA */}
        <div className="px-8 pb-10 pt-6 border-t border-gray-100 space-y-4" data-id="element-82" data-mp="39yxe4jpowdhp">
          <a href="https://meelo.kz/home" target="_blank" rel="noopener noreferrer" onClick={close} className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-base font-semibold text-[#7C4DFF] border border-[#7C4DFF]/30 bg-[#7C4DFF]/5 hover:bg-[#7C4DFF] hover:text-white transition-all" data-id="element-83" data-mp="39wszp7u5zw36">
            <User size={18} strokeWidth={2} data-id="element-84" data-mp="1on540azqqstj" />
            {t('nav.signin')}
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={close} className="block" data-id="element-85" data-mp="1ju27pv3kx59n">
            <p className="text-2xl font-bold text-[#7C4DFF] tracking-tight mb-1" data-id="element-86" data-mp="1tjlomv7qep1v">
              {t('nav.phone')}
            </p>
            <p className="text-sm text-gray-400 font-medium" data-id="element-87" data-mp="27jn1l919tmxp">WhatsApp</p>
          </a>
        </div>
      </div>

      {/* ── Pill navbar ───────────────────────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4 sm:pt-5" data-id="element-88" data-mp="d6x64mv985o7">
        <nav className={`w-full max-w-[1000px] transition-all duration-300 rounded-full border border-white/30 shadow-[0_1px_12px_-2px_rgba(0,0,0,0.06)] backdrop-blur-2xl ${isScrolled ? 'bg-white/30' : 'bg-white/20'}`} data-id="element-89" data-mp="88lty5hhlj2t">
          <div className="flex items-center h-14 px-5 sm:px-7" data-id="element-90" data-mp="2ovlbj23shf3b">
            <a href="#" className="flex-shrink-0 mr-auto md:mr-0" data-id="element-91" data-mp="2a4b8ew71ypv5">
              <img src="https://cdn.magicpatterns.com/uploads/k1HQQhYUAYkCqHQP8zEE8y/meelo_logo_transparent.png" alt="Meelo" className="h-10 sm:h-12 w-auto" data-id="element-92" data-mp="2f35ohqdmy0bk" />
            </a>

            {/* Desktop links */}
            <div className="hidden md:flex items-center justify-end flex-1 gap-1" data-id="element-93" data-mp="1r6030fj6b5he">
              {SECTIONS.map(({
              id,
              key,
              href
            }) => <a key={id} href={href ?? `#${id}`} className={linkClass(id)} data-id="element-94" data-mp="1w64ic28chu61">
                  {t(key)}
                </a>)}
              <a href="#/mission" className="text-sm font-medium text-gray-700 hover:text-[#7C4DFF] transition-colors whitespace-nowrap px-3 py-1" data-id="element-95" data-mp="3lhx0ap4ar5kd">
                {t('nav.mission')}
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#7C4DFF] hover:text-[#651FFF] transition-colors whitespace-nowrap px-3 py-1 ml-4" data-id="element-96" data-mp="29aebwkwan15d">
                {t('nav.phone')}
              </a>
              <a href="https://meelo.kz/home" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 ml-3 px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 text-[#7C4DFF] border border-[#7C4DFF]/30 bg-[#7C4DFF]/[0.06] backdrop-blur-sm hover:bg-gradient-to-r hover:from-[#7C4DFF] hover:to-[#651FFF] hover:text-white hover:border-transparent hover:shadow-[0_0_16px_rgba(124,77,255,0.3)]" data-id="element-97" data-mp="1un2yedgjx72m">
                <User size={14} strokeWidth={2.2} className="flex-shrink-0" data-id="element-98" data-mp="3ijspdzucc50d" />
                {t('nav.signin')}
              </a>
              <div className="ml-2 pl-2 border-l border-gray-200" data-id="element-99" data-mp="3nhkg4qj91l2z">
                <LanguageSwitcher data-id="element-100" data-mp="3s9lzeweu4c2i" />
              </div>
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden text-gray-600 hover:text-[#7C4DFF] transition-colors" aria-label="Открыть меню" data-id="element-101" data-mp="13kl8ezkp2cn">
              <Menu size={22} data-id="element-102" data-mp="34kt0cr2x1o3o" />
            </button>
          </div>
        </nav>
      </div>
    </>;
}